import { useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { runOwnerAction } from '../../../services/ownerConsoleService'
import { OwnerFirmConfirm } from '../kit/OwnerConfirm'
import { useDemoFreeze } from './useDemoFreeze'

const MIN_HOURS = 1
const MAX_HOURS = 24
const shortDate = (millis: number): string => new Date(millis).toISOString().slice(0, 16).replace('T', ' ')
const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

type Pending = { active: true; hours: number } | { active: false }

/** Demo freeze control (R35 DL5.b): start now for N hours, end now. Deploy scripts refuse while it is active. */
export function DemoFreezeControl({ onChanged }: { onChanged: () => void }) {
  const { t } = useNls()
  const { freeze, loading, error } = useDemoFreeze(true)
  const [hours, setHours] = useState('4')
  const [note, setNote] = useState('')
  const [pending, setPending] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const hoursValue = Number(hours)
  const hoursValid = hours.trim() !== '' && Number.isFinite(hoursValue) && hoursValue >= MIN_HOURS && hoursValue <= MAX_HOURS

  const confirm = async (reason: string) => {
    if (!pending) return
    setBusy(true)
    setActionError(null)
    setDone(null)
    try {
      if (pending.active) {
        await runOwnerAction({ kind: 'SET_DEMO_FREEZE', reason, active: true, hours: pending.hours, note: note.trim() })
        setDone(t('NLSOwnerConsoleDemoLaneFreezeStarted'))
      } else {
        await runOwnerAction({ kind: 'SET_DEMO_FREEZE', reason, active: false })
        setDone(t('NLSOwnerConsoleDemoLaneFreezeEnded'))
      }
      setPending(null)
      onChanged()
    } catch (err) {
      setActionError(messageOf(err))
      setPending(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="ownerDemoLaneSection ownerDemoFreeze" aria-label={t('NLSOwnerConsoleDemoLaneFreezeTitle')} data-testid="demo-freeze-control">
      <div>
        <h3>{t('NLSOwnerConsoleDemoLaneFreezeTitle')}</h3>
        <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneFreezeHint')}</p>
      </div>

      {loading ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneLoading')}</p> : null}
      {error ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLaneFreezeLoadFailed')}</strong>
          <p className="ownerDemoLaneError">{error}</p>
        </div>
      ) : null}

      {freeze ? (
        freeze.active && freeze.untilMillis !== null ? (
          <div data-testid="demo-freeze-state" data-active="true">
            <strong>{t('NLSOwnerConsoleDemoLaneFreezeActive', { until: shortDate(freeze.untilMillis) })}</strong>
            {freeze.note ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneFreezeNote', { note: freeze.note })}</p> : null}
            {freeze.setBy ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneFreezeSetBy', { user: freeze.setBy })}</p> : null}
            <button
              type="button"
              className="ownerModalBtn ownerModalBtn--default"
              disabled={busy}
              onClick={() => { setActionError(null); setDone(null); setPending({ active: false }) }}
            >
              {t('NLSOwnerConsoleDemoLaneFreezeEnd')}
            </button>
          </div>
        ) : (
          <p data-testid="demo-freeze-state" data-active="false">{t('NLSOwnerConsoleDemoLaneFreezeNone')}</p>
        )
      ) : null}

      <div className="ownerDemoFreezeForm">
        <label>
          <span>{t('NLSOwnerConsoleDemoLaneFreezeHours')}</span>
          <input type="number" min={MIN_HOURS} max={MAX_HOURS} step={1} value={hours} onChange={(event) => setHours(event.target.value)} />
        </label>
        <label>
          <span>{t('NLSOwnerConsoleDemoLaneFreezeNoteLabel')}</span>
          <input type="text" maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
        <button
          type="button"
          className="ownerModalBtn ownerModalBtn--default"
          disabled={!hoursValid || busy}
          onClick={() => { setActionError(null); setDone(null); setPending({ active: true, hours: hoursValue }) }}
        >
          {t('NLSOwnerConsoleDemoLaneFreezeStart')}
        </button>
      </div>
      {!hoursValid ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneFreezeHoursInvalid')}</p> : null}

      {done ? <p className="ownerDemoLaneNote" role="status">{done}</p> : null}
      {actionError ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLaneFreezeFailed')}</strong>
          <p className="ownerDemoLaneError">{actionError}</p>
        </div>
      ) : null}

      <OwnerFirmConfirm
        isOpen={pending !== null}
        title={pending?.active === false ? t('NLSOwnerConsoleDemoLaneFreezeEndTitle') : t('NLSOwnerConsoleDemoLaneFreezeStartTitle')}
        description={pending?.active === false
          ? t('NLSOwnerConsoleDemoLaneFreezeEndDescription')
          : pending ? t('NLSOwnerConsoleDemoLaneFreezeStartDescription', { hours: String(pending.hours) }) : undefined}
        confirmLabel={pending?.active === false ? t('NLSOwnerConsoleDemoLaneFreezeEnd') : t('NLSOwnerConsoleDemoLaneFreezeStart')}
        busy={busy}
        onClose={() => setPending(null)}
        onConfirm={confirm}
      />
    </section>
  )
}
